/**
 * Projects — list surface.
 *
 * Route: /projects
 *
 * Iteration 2026-07-23 — REEDOS approved TIP § 8. Zero financial fields
 * on this surface. Only engagements the caller is a participant of / is
 * in the authorised reporting subtree of / holds project:read_all
 * are returned by the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Plus, Buildings, User, Briefcase, ShieldWarning, ClipboardText, CheckCircle } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import ProjectCreateDialog from "./components/ProjectCreateDialog";
import EmergencyOverrideDialog from "./components/EmergencyOverrideDialog";
import RequestSubmittedDialog from "./components/RequestSubmittedDialog";
import {
    formatDate, formatUserName, StateBadge, PriorityBadge, PageLoading,
} from "./components/ui";


export default function ProjectsList() {
    const { user } = useAuth();
    const perms = new Set(user?.permissions || []);
    // W3-A · B7 · Project Initiation governance (2026-08-02).
    //
    // The "Initiate Project" (formerly "Create Project") button is
    // gated by ``project:initiate`` — the target-specific initiation
    // capability introduced in B5.  Legacy ``project:create`` NO
    // LONGER controls this UI: submitting the form no longer creates
    // an Active Project directly — it opens a workflow request that
    // must be approved before the engagement is materialised.  The
    // 202 Accepted response is handled by RequestSubmittedDialog.
    //
    // Only holders of ``workflow:emergency_override`` see the
    // governed override entry point — that is the ONLY UI path that
    // can create an Active Project without approval.  Approvers see
    // a link to the Approvals Queue for pending requests.
    const canInitiate = perms.has("project:initiate");
    const canEmergencyOverride = perms.has("workflow:emergency_override");
    const canApprove = perms.has("project:approve");
    const canReadOwnWorkflow = perms.has("workflow:read_own");
    // W3-A · B7 · Caller may hold ONLY `project:initiate` without
    // `project:read` (e.g. hr_user).  In that case we still let them
    // reach this page (route is OR-gated in App.js) but we skip the
    // engagement-list backend call — the list endpoint requires
    // `project:read` — and render an empty state so the initiation
    // action stays reachable.
    const canReadProjects = perms.has("project:read");

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [projectTypeFilter, setProjectTypeFilter] = useState("all");
    const [createOpen, setCreateOpen] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    // Holds the 202 response from POST /engagement so
    // RequestSubmittedDialog can display the pending handle.
    const [submittedRequest, setSubmittedRequest] = useState(null);

    const load = async () => {
        if (!canReadProjects) {
            // Initiator-only role (no project:read).  Show an empty
            // list; header actions remain visible so the caller can
            // still submit a Project Request or reach My Project
            // Requests.
            setItems([]);
            setTotal(0);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (projectTypeFilter !== "all") params.set("project_type", projectTypeFilter);
            params.set("limit", "50");
            const r = await api.get(`/projects/engagement/list?${params}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // debounce search + type filter
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [q, projectTypeFilter]);

    const filtered = useMemo(() => items, [items]);

    return (
        <div className="space-y-6" data-testid="projects-list-page">
            <PageHeader
                eyebrow="Delivery"
                title="Projects"
                description="Cross-project workspace for Client and Internal engagements — assignment, delegation, and collaboration in one place."
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            asChild
                            data-testid="op-my-work-btn"
                        >
                            <Link to="/projects/my-work">
                                <Briefcase className="w-4 h-4 mr-1" />
                                My Work
                            </Link>
                        </Button>
                        {canReadOwnWorkflow ? (
                            <Button
                                variant="outline"
                                asChild
                                data-testid="op-my-requests-btn"
                            >
                                <Link to="/workflows/my-requests">
                                    <ClipboardText className="w-4 h-4 mr-1" />
                                    My Project Requests
                                </Link>
                            </Button>
                        ) : null}
                        {canApprove ? (
                            <Button
                                variant="outline"
                                asChild
                                data-testid="op-approvals-queue-btn"
                            >
                                <Link to="/workflows/approvals">
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Project Approvals
                                </Link>
                            </Button>
                        ) : null}
                        {canEmergencyOverride ? (
                            <Button
                                variant="outline"
                                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                                onClick={() => setOverrideOpen(true)}
                                data-testid="op-emergency-override-btn"
                            >
                                <ShieldWarning className="w-4 h-4 mr-1" />
                                Emergency Project Creation
                            </Button>
                        ) : null}
                        {canInitiate ? (
                            <Button
                                data-testid="op-initiate-project-btn"
                                onClick={() => setCreateOpen(true)}
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Initiate Project
                            </Button>
                        ) : null}
                    </div>
                }
            />

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full max-w-sm">
                    <MagnifyingGlass className="absolute top-2.5 left-3 w-4 h-4 text-slate-400" />
                    <Input
                        data-testid="op-search-input"
                        className="pl-9"
                        placeholder="Search engagements by name or number..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-slate-500">Type</span>
                    <Select
                        value={projectTypeFilter}
                        onValueChange={(v) => setProjectTypeFilter(v)}
                    >
                        <SelectTrigger
                            className="w-[140px]"
                            data-testid="project-type-filter-trigger"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="project-type-filter-all">All</SelectItem>
                            <SelectItem value="client" data-testid="project-type-filter-client">Client</SelectItem>
                            <SelectItem value="internal" data-testid="project-type-filter-internal">Internal</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-slate-500" data-testid="op-total-count">
                    {total} engagement{total === 1 ? "" : "s"} in your scope
                </span>
            </div>

            {loading ? (
                <PageLoading label="Loading engagements…" testId="op-loading" />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Buildings className="w-6 h-6" />}
                    title={q || projectTypeFilter !== "all" ? "No matches" : "No Projects Yet"}
                    description={
                        q || projectTypeFilter !== "all"
                            ? "Try clearing your search or type filter."
                            : "Create a project to initiate customer implementation, customization, POC, support, or an internal initiative."
                    }
                    action={
                        !q && projectTypeFilter === "all" && canInitiate ? (
                            <Button
                                data-testid="op-empty-initiate-project-btn"
                                onClick={() => setCreateOpen(true)}
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Initiate Project
                            </Button>
                        ) : null
                    }
                />
            ) : (
                <div className="overflow-x-auto border border-slate-200 rounded-md">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Number</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Name</th>
                                <th className="text-left px-3 py-2">Context</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Priority</th>
                                <th className="text-left px-3 py-2">Coordinator</th>
                                <th className="text-left px-3 py-2">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`op-engagement-row-${it.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        <Link className="text-emerald-600 hover:underline" to={`/projects/${it.id}`} data-testid={`op-engagement-link-${it.id}`}>
                                            {it.engagement_number}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2" data-testid={`project-type-cell-${it.id}`}>
                                        {it.project_type === "internal" ? (
                                            <span
                                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                                                data-testid={`project-type-badge-internal-${it.id}`}
                                            >
                                                <Briefcase className="w-3 h-3" />
                                                Internal
                                            </span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                data-testid={`project-type-badge-client-${it.id}`}
                                            >
                                                <Buildings className="w-3 h-3" />
                                                Client
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-900">{it.name}</td>
                                    <td className="px-3 py-2 text-slate-700">
                                        {it.client_snapshot?.name ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Buildings className="w-3.5 h-3.5 text-slate-400" />
                                                <span className="truncate max-w-[10rem]">{it.client_snapshot.name}</span>
                                            </span>
                                        ) : it.client_id ? (
                                            <span className="inline-flex items-center gap-1 text-slate-600">
                                                <Buildings className="w-3.5 h-3.5 text-slate-400" />
                                                Client
                                            </span>
                                        ) : it.prospect_id ? (
                                            <span className="inline-flex items-center gap-1 text-slate-600">
                                                <User className="w-3.5 h-3.5 text-slate-400" />
                                                Prospect
                                            </span>
                                        ) : it.project_type === "internal" && it.organisational_context ? (
                                            <span
                                                className="inline-flex items-center gap-1 text-xs text-slate-600"
                                                data-testid={`project-org-context-${it.id}`}
                                            >
                                                <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                                                {it.organisational_context.business_unit}
                                            </span>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <StateBadge state={it.status} testId={`op-status-${it.id}`} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <PriorityBadge priority={it.priority} testId={`op-priority-${it.id}`} />
                                    </td>
                                    <td className="px-3 py-2 text-slate-700 truncate max-w-[10rem]">
                                        {formatUserName(it.engagement_coordinator_snapshot, it.engagement_coordinator_user_id)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 text-xs">{formatDate(it.updated_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ProjectCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSubmitted={(pendingRequest) => {
                    // W3-A · B7 · workflow returned 202 pending.
                    // Close the initiation dialog and show the
                    // pending-request confirmation screen with the
                    // workflow instance handle.
                    setCreateOpen(false);
                    setSubmittedRequest(pendingRequest);
                    // No engagement was created — do NOT reload the
                    // active-projects list; the workflow row will
                    // materialise once an approver acts.
                }}
            />
            <RequestSubmittedDialog
                open={!!submittedRequest}
                request={submittedRequest}
                onClose={() => setSubmittedRequest(null)}
            />
            <EmergencyOverrideDialog
                open={overrideOpen}
                onClose={() => setOverrideOpen(false)}
                onCreated={() => {
                    setOverrideOpen(false);
                    toast.success("Active Project created via Emergency Project Creation.");
                    load();
                }}
            />
        </div>
    );
}
